import React from 'react';
import Image from 'next/image';

/**
 * Componente para otimizar imagens usando o componente Image do Next.js
 * 
 * @param {Object} props - Propriedades do componente
 * @param {string} props.src - URL da imagem
 * @param {string} props.alt - Texto alternativo para a imagem
 * @param {number} props.width - Largura da imagem
 * @param {number} props.height - Altura da imagem
 * @param {string} props.className - Classes CSS para a imagem
 * @param {Object} props.style - Estilos inline para a imagem
 * @param {string} props.priority - Define se a imagem deve ser carregada com prioridade
 * @param {string} props.loading - Define o comportamento de carregamento da imagem
 * @param {string} props.objectFit - Define como a imagem deve se ajustar ao container
 * @param {string} props.objectPosition - Define a posição da imagem dentro do container
 * @param {string} props.quality - Define a qualidade da imagem (1-100)
 * @returns {React.Component} - Componente Image otimizado
 */
const ImageOptimizer = ({
  src,
  alt,
  width,
  height,
  className,
  style,
  priority = false,
  loading = 'lazy',
  objectFit = 'contain',
  objectPosition = 'center',
  quality = 75,
  ...props
}) => {
  // Verificar se a URL é externa
  const isExternal = src?.startsWith('http') || src?.startsWith('//');
  
  // Converter URLs sem protocolo para HTTPS
  if (src?.startsWith('//')) {
    src = `https:${src}`;
  }

  // Definir dimensões padrão se não forem fornecidas
  const imgWidth = width || 500;
  const imgHeight = height || 300;

  // Usar o componente Image do Next.js para imagens locais
  if (!isExternal) {
    return (
      <Image
        src={src}
        alt={alt || ''}
        width={imgWidth}
        height={imgHeight}
        className={className}
        style={{
          objectFit,
          objectPosition,
          ...style
        }}
        priority={priority}
        loading={loading}
        quality={quality}
        {...props}
      />
    );
  }

  // Para imagens externas, usar o componente Image com domínios permitidos
  // ou fallback para tag img com atributos de otimização
  return (
    <div className="image-container" style={{position: 'relative', display: 'block', ...style}}>
      <img
        src={src}
        alt={alt || ''}
        width={width}
        height={height}
        className={className}
        style={{
          objectFit,
          objectPosition,
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
          visibility: 'visible',
          opacity: 1
        }}
        loading={loading}
        decoding="async"
        {...props}
      />
    </div>
  );
};

export default ImageOptimizer;
